import React from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import './DeleteConfirmationModal.css';

/** @typedef {'cascade' | 'unlink'} RelatedRecordsDeleteMode */

const DeleteConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  title = 'Confirm Delete',
  message = 'Are you sure you want to delete this item?',
  itemName = '',
  confirmText = 'Delete',
  cancelText = 'Cancel',
  variant = 'danger', // danger, warning
  // When set, show result in modal (success or error) with only Close button
  resultMessage = null,
  resultVariant = 'danger', // 'success' | 'danger'
  isChecking = false,
  checkingMessage = 'Checking...',
  isDeleting = false, // show loader and disable buttons until response
  loadingMessage = 'Deleting...',
  // When set, show blocked/references content with only Close button
  blockedContent = null,
  blockedTitle = null,
  size,
  // Optional: how to handle related (child) records — mutually exclusive
  showRelatedRecordsOptions = false,
  /** @type {RelatedRecordsDeleteMode} */
  relatedRecordsMode = 'unlink',
  onRelatedRecordsModeChange = () => {},
}) => {
  const handleConfirm = async () => {
    const result = onConfirm();
    if (result && typeof result.then === 'function') {
      await result;
    }
    // Parent controls closing (e.g. after showing result in modal)
  };

  const getIconColor = () => {
    if (variant === 'warning') {
      return getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#243A73';
    }
    return '#dc3545';
  };

  const showingBlocked = blockedContent != null;
  const showingResult = !showingBlocked && resultMessage != null && resultMessage.trim() !== '';
  const showingLoader = !showingBlocked && !showingResult && (isChecking || isDeleting);
  const isBusy = isChecking || isDeleting;
  const modalTitle = showingBlocked
    ? (blockedTitle || title)
    : showingResult
      ? (resultVariant === 'success' ? 'Result' : 'Error')
      : title;

  const handleHide = () => {
    if (isBusy) return;
    onHide();
  };

  const setMode = mode => {
    if (!isBusy) onRelatedRecordsModeChange(mode);
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      backdrop="static"
      keyboard={!isBusy}
      size={size || (showingBlocked ? 'lg' : undefined)}
      className="delete-confirmation-modal"
    >
      <Modal.Header closeButton={!isBusy}>
        <Modal.Title>
          {showingResult ? (
            resultVariant === 'success' ? (
              <><FaCheckCircle className="me-2 text-success" /> Result</>
            ) : (
              <><FaExclamationTriangle className="me-2" style={{ color: '#dc3545' }} /> Error</>
            )
          ) : (
            <><FaExclamationTriangle className="me-2" style={{ color: getIconColor() }} /> {modalTitle}</>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showingBlocked ? (
          blockedContent
        ) : showingResult ? (
          <Alert variant={resultVariant} className="mb-0">
            {resultMessage}
          </Alert>
        ) : showingLoader ? (
          <div className="d-flex align-items-center gap-2 py-2">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <span>{isChecking ? checkingMessage : loadingMessage}</span>
          </div>
        ) : (
          <>
            <p className="mb-0">
              {message}
              {itemName && (
                <strong className="d-block mt-2 text-dark">{itemName}</strong>
              )}
            </p>
            {showRelatedRecordsOptions && (
              <div className="mt-3 text-start">
                <div className="small text-muted mb-2">Related records</div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="delete-related-records-mode"
                    id="delete-related-cascade"
                    checked={relatedRecordsMode === 'cascade'}
                    onChange={() => setMode('cascade')}
                    disabled={isBusy}
                  />
                  <label className="form-check-label" htmlFor="delete-related-cascade">
                    Delete related (child) records
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="delete-related-records-mode"
                    id="delete-related-unlink"
                    checked={relatedRecordsMode === 'unlink'}
                    onChange={() => setMode('unlink')}
                    disabled={isBusy}
                  />
                  <label className="form-check-label" htmlFor="delete-related-unlink">
                    Remove references and keep related records (clear links to this record)
                  </label>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {showingBlocked || showingResult ? (
          <Button variant="primary" onClick={handleHide}>
            Close
          </Button>
        ) : showingLoader ? (
          <Button variant="secondary" disabled>
            {cancelText}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleHide} disabled={isBusy}>
              {cancelText}
            </Button>
            <Button variant={variant} onClick={handleConfirm} disabled={isBusy}>
              {confirmText}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteConfirmationModal;
